import * as React from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import environment from 'api/environment';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import AnchoredPanel from 'sharedComponents/core/AnchoredPanel';
import { ClassroomInstanceSelectorQueryResponse } from './__generated__/ClassroomInstanceSelectorQuery.graphql';
import { DropdownOption } from 'sharedComponents/core/Input/Dropdown/Dropdown';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: 10
  },
  clickableBox: {
    height: 40,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gridGap: 12,
    padding: [0, 14],
    border: '1px solid #ededed',
    borderRadius: 8,
    cursor: 'pointer',
    background: 'white',
    transition: 'border-color 0.15s',
    '&:hover': {
      borderColor: '#d7dae0'
    }
  },
  boxText: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.colors.primaryBlack,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  boxChevron: {
    fontSize: 11,
    color: '#74777f',
    flexShrink: 0,
    transition: 'transform 0.15s ease-out'
  },
  panel: {
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
    padding: 6,
    maxHeight: 260,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gridGap: 2
  },
  option: {
    padding: '9px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: theme.colors.primaryBlack,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      background: '#f8f9fb'
    }
  },
  optionSelected: {
    extend: 'option',
    background: '#f0f1f3'
  }
}));

type Instance = {
  uuid: string;
  startDate: string;
  endDate: string;
};

type Props = {
  courseID: number;
  onSelected: (instance: Instance) => void;
};

function ClassroomInstanceSelector({ onSelected, courseID }: Props) {
  const [currentDate] = React.useState(new Date());
  const [selected, setSelected] = React.useState<DropdownOption>();
  return (
    <QueryRenderer
      environment={environment}
      query={graphql`
        query ClassroomInstanceSelectorQuery(
          $courseID: Int!
          $afterDate: Time
        ) {
          course(id: $courseID) {
            ... on ClassroomCourse {
              instances(filter: { afterDate: $afterDate }) {
                edges {
                  uuid
                  startDate
                  endDate
                }
              }
            }
          }
        }
      `}
      variables={{
        courseID: courseID,
        afterDate: currentDate
      }}
      render={({ error, props }) => {
        if (props) {
          const { course } = props as ClassroomInstanceSelectorQueryResponse;
          if (course?.instances) {
            const dropdownOptions =
              course?.instances.edges
                ?.map((instance) => ({
                  title: `${moment(instance?.startDate).format(
                    'DD MMMM YYYY hh:mm A'
                  )} - ${moment(instance?.endDate).format(
                    'DD MMMM YYYY hh:mm A'
                  )}`,
                  id: instance?.uuid ?? '',
                  ...instance
                }))
                .reverse() ?? [];

            return (
              <Selector
                dropdownOptions={dropdownOptions}
                selected={selected}
                setSelected={(current: DropdownOption) => {
                  const selected = course.instances?.edges?.find(
                    (instance) => instance?.uuid == current.id
                  );

                  if (selected) onSelected(selected);
                  setSelected(current);
                }}
              />
            );
          }
        }
        return false;
      }}
    />
  );
}

type SelectorProps = {
  dropdownOptions: DropdownOption[];
  selected: any;
  setSelected: (current: DropdownOption) => void;
};
function Selector({ dropdownOptions, selected, setSelected }: SelectorProps) {
  const classes = useStyles();
  const [isOpen, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (selected) {
      setSelected(selected);
    } else if (dropdownOptions.length > 0) {
      setSelected(dropdownOptions[0]);
    }
  }, [selected, dropdownOptions]);

  // Close when clicking anywhere outside. The panel is portaled to
  // document.body, so check it separately.
  React.useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (evt: MouseEvent) => {
      const target = evt.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const current = selected || dropdownOptions[0];

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.clickableBox} onClick={() => setOpen((o) => !o)}>
        <span className={classes.boxText}>
          {current ? current.title : 'Course instance'}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={classes.boxChevron}
          style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}
        />
      </div>
      <AnchoredPanel
        anchorRef={containerRef}
        open={isOpen}
        offset={8}
        panelRef={panelRef}
      >
        <div className={classes.panel}>
          {dropdownOptions.map((option) => (
            <div
              key={option.id}
              className={
                current && option.id === current.id
                  ? classes.optionSelected
                  : classes.option
              }
              onClick={() => {
                setSelected(option);
                setOpen(false);
              }}
            >
              {option.title}
            </div>
          ))}
        </div>
      </AnchoredPanel>
    </div>
  );
}

export default React.memo(ClassroomInstanceSelector);
